import { useEffect, useRef, useState } from 'react';

/**
 * 로딩 표시를 위한 훅.
 * - `active`가 true 가 되는 즉시 true 를 반환한다.
 * - `active`가 false 가 된 뒤에도 최소 `minMs` 동안은 true 를 유지해, 빠른 로딩에서
 *   스켈레톤이 번쩍이는(깜빡이는) 현상을 막는다.
 *
 * 표시 여부는 effect 가 아니라 렌더 중 ref 로 판단한다. effect 로 지연 세팅하면
 * useDeferredValue 백그라운드 렌더와 경합해 시작 지점에 1프레임 빈 화면이 생기기 때문.
 *
 * @param active 로딩 여부(예: isDataLoading)
 * @param minMs 최소 표시 시간(기본 500ms)
 */
export function useMinimumLoading(active: boolean, minMs = 500) {
  const startedAtRef = useRef<number | null>(null);
  const [, forceUpdate] = useState(0);

  // 로딩이 시작되면 시작 시각을 렌더 중 즉시 기록한다(빈 프레임 방지).
  if (active && startedAtRef.current === null) {
    startedAtRef.current = Date.now();
  }

  const show = active || startedAtRef.current !== null;

  useEffect(() => {
    if (active || startedAtRef.current === null) return;
    // active 가 끝났으니 최소 표시 시간의 잔여분만큼 유지 후 종료
    const remaining = Math.max(0, minMs - (Date.now() - startedAtRef.current));
    const timer = setTimeout(() => {
      startedAtRef.current = null;
      forceUpdate(n => n + 1);
    }, remaining);
    return () => clearTimeout(timer);
  }, [active, minMs]);

  return show;
}
